import React, { useState, useEffect, useCallback } from "react"
import {
  Result,
  Button,
  Typography,
  Input,
  Alert,
  Spin,
  Card,
  Space,
} from "@mimber/ui-web"
import { message } from "@mimber/ui-web"
import {
  useStepUpStatus,
  useInitiateStepUp,
  useVerifyStepUp,
} from "@mimber/client/mimber-organization"
import type { StepUpMethod } from "@mimber/schema"
import type { MFAStepUpGateProps } from "./MFAStepUpGate.types"

const { Title, Text } = Typography

const METHOD_DESCRIPTIONS: Record<StepUpMethod, string> = {
  totp: "Enter the 6-digit code from your authenticator app",
  sms: "Enter the 6-digit code sent to your phone",
  email: "Enter the 6-digit code sent to your email",
}

const RESEND_COOLDOWN_SECONDS = 60

export const MFAStepUpGate: React.FunctionComponent<MFAStepUpGateProps> = ({
  children,
}) => {
  const [code, setCode] = useState("")
  const [method, setMethod] = useState<StepUpMethod | null>(null)
  const [resendCooldown, setResendCooldown] = useState(0)

  const statusQuery = useStepUpStatus()

  const initiateMutation = useInitiateStepUp({
    onSuccess: (response) => {
      setMethod(response.data.method)
      if (response.data.method === "email" || response.data.method === "sms") {
        setResendCooldown(RESEND_COOLDOWN_SECONDS)
      }
    },
    onError: (error) => {
      message.error(error.message || "Failed to initiate verification")
    },
  })

  const verifyMutation = useVerifyStepUp({
    onSuccess: () => {
      message.success("Identity verified successfully")
    },
    onError: (error) => {
      setCode("")
      message.error(error.message || "Invalid verification code")
    },
  })

  // Auto-initiate on mount when not verified
  useEffect(() => {
    if (
      statusQuery.data &&
      !statusQuery.data.data.verified &&
      !initiateMutation.isPending &&
      !initiateMutation.isSuccess &&
      !initiateMutation.isError
    ) {
      initiateMutation.mutate()
    }
  }, [statusQuery.data])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [resendCooldown])

  // Auto-submit when 6 digits entered
  const handleCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, "")
      setCode(value)
      if (value.length === 6 && method && !verifyMutation.isPending) {
        verifyMutation.mutate({ code: value, method })
      }
    },
    [method, verifyMutation]
  )

  const handleResend = useCallback(() => {
    setCode("")
    initiateMutation.mutate(undefined, {
      onSuccess: () => {
        setResendCooldown(RESEND_COOLDOWN_SECONDS)
      },
    })
  }, [initiateMutation])

  // Loading state while checking status
  if (statusQuery.isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  // Error state — status query failed
  if (statusQuery.isError) {
    return (
      <Card style={{ maxWidth: 480, margin: "0 auto" }}>
        <Result
          status="error"
          title="Unable to verify identity"
          subTitle="Something went wrong. Please try again."
          extra={
            <Button type="primary" onClick={() => statusQuery.refetch()}>
              Retry
            </Button>
          }
        />
      </Card>
    )
  }

  // Already verified — render children
  if (statusQuery.data?.data.verified) {
    return <>{children}</>
  }

  // Verification UI
  return (
    <Card style={{ maxWidth: 480, margin: "0 auto" }}>
      <Result
        status="info"
        title="Verify your identity"
        subTitle={
          method ? METHOD_DESCRIPTIONS[method] : "Preparing verification..."
        }
      />

      {initiateMutation.isPending && !method && (
        <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
          <Spin />
        </div>
      )}

      {method && (
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          {verifyMutation.isError && (
            <Alert
              type="error"
              showIcon
              message="Invalid code. Please try again."
            />
          )}

          <div>
            <Title level={5} style={{ marginBottom: 8 }}>
              Enter Verification Code
            </Title>
            <Input
              size="large"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={handleCodeChange}
              disabled={verifyMutation.isPending}
              style={{
                textAlign: "center",
                letterSpacing: "8px",
                fontSize: "24px",
                fontFamily: "monospace",
              }}
            />
          </div>

          <Button
            type="primary"
            block
            disabled={code.length !== 6}
            loading={verifyMutation.isPending}
            onClick={() => method && verifyMutation.mutate({ code, method })}
          >
            Verify
          </Button>

          {(method === "email" || method === "sms") && (
            <Text type="secondary" style={{ textAlign: "center" }}>
              Didn&apos;t receive the code?{" "}
              {resendCooldown > 0 ? (
                <Text type="secondary">Resend in {resendCooldown}s</Text>
              ) : (
                <Button
                  type="link"
                  size="small"
                  disabled={initiateMutation.isPending}
                  onClick={handleResend}
                  style={{ padding: 0 }}
                >
                  Resend
                </Button>
              )}
            </Text>
          )}
        </Space>
      )}
    </Card>
  )
}
